import { EmojiEvents, Refresh } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import React from "react";

import GroupsDisplay from "../../../components/GroupsDisplay";
import TimezoneDisplay from "../../../components/TimezoneDisplay";
import { RankingOrder } from "../../../types/callRanking";
import { CONVERSATION_LENS } from "../../../utils/terminology";
import { getTimeRangeLabel } from "../../../utils/timerange";

const CHIP_BACKGROUND_OPACITY = 0.05;

interface RankedCallsContextBannerProps {
  order: RankingOrder;
  lens: string;
  attribute?: string;
  count: number;
  timeRange: string;
  agent?: string;
  totalResults: number;
  onReopenWizard: () => void;
  onRefresh: () => void;
}

const SINGLE_RESULT = 1;

interface FilterChip {
  label: string;
  value: string;
}

interface BuildFiltersParams {
  order: RankingOrder;
  lens: string;
  attribute?: string;
  timeRange: string;
  agent?: string;
  count: number;
}

const buildFilters = (params: BuildFiltersParams): FilterChip[] => [
  { label: "Ranking Focus", value: params.order === RankingOrder.BestFirst ? "Top Performing" : "Lowest Performing" },
  { label: CONVERSATION_LENS, value: params.lens },
  ...(params.attribute ? [{ label: "Attribute", value: params.attribute }] : []),
  { label: "Timeframe", value: getTimeRangeLabel(params.timeRange) },
  ...(params.agent ? [{ label: "Agent", value: params.agent }] : []),
  { label: "Results", value: `${params.order === RankingOrder.BestFirst ? "Top" : "Bottom"} ${params.count}` }
];

interface BannerHeaderProps {
  totalResults: number;
  onRefresh: () => void;
}

const BannerHeader = ({ totalResults, onRefresh }: BannerHeaderProps): React.ReactElement => (
  <Box sx={{ p: 2, display: "flex", alignItems: "center" }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 1,
          backgroundColor: "primary.main",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <EmojiEvents sx={{ color: "white", fontSize: 20 }} />
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={600}>
          Find Best & Worst Calls
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {totalResults} {totalResults === SINGLE_RESULT ? "result" : "results"} found
        </Typography>
      </Box>
    </Box>
    <Box sx={{ marginLeft: "auto", display: "flex", gap: 1, alignItems: "center" }}>
      <GroupsDisplay showLabel={true} />
      <TimezoneDisplay showTime={true} />
      <Tooltip title="Refresh">
        <IconButton onClick={onRefresh}>
          <Refresh />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
);

const RankedCallsContextBanner = ({
  order,
  lens,
  attribute,
  count,
  timeRange,
  agent,
  totalResults,
  onReopenWizard,
  onRefresh
}: RankedCallsContextBannerProps): React.ReactElement => {
  const theme = useTheme();
  const filters = buildFilters({ order, lens, attribute, timeRange, agent, count });

  return (
    <Paper sx={{ mb: 4 }}>
      <BannerHeader totalResults={totalResults} onRefresh={onRefresh} />
      <Box sx={{ px: 2, pb: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
        {filters.map((filter) => (
          <Chip
            key={filter.label}
            label={`${filter.label}: ${filter.value}`}
            size="small"
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, CHIP_BACKGROUND_OPACITY),
              borderRadius: "16px",
              border: "1px solid",
              borderColor: "primary.main",
              fontWeight: 400,
              height: 24,
              // eslint-disable-next-line @typescript-eslint/naming-convention
              "& .MuiChip-label": {
                paddingLeft: 1,
                paddingRight: 1
              }
            }}
          />
        ))}
        <Button
          variant="outlined"
          size="small"
          startIcon={<EmojiEvents />}
          onClick={onReopenWizard}
          sx={{ ml: "auto", textTransform: "none" }}
        >
          Return to Wizard
        </Button>
      </Box>
    </Paper>
  );
};

export default RankedCallsContextBanner;
